export type ApiMeta = {
    page?: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
};

type ApiEnvelope<T> =
    | { success: true; data: T; meta?: ApiMeta }
    | { success: false; error: { code: string; message: string; details?: unknown } };

export class ApiError extends Error {
    constructor(
        public readonly code: string,
        message: string,
        public readonly status: number,
        public readonly details?: unknown,
    ) {
        super(message);
        this.name = "ApiError";
    }

    get isUnauthorized(): boolean {
        return this.status === 401;
    }
}

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

async function request<T>(path: string, options: RequestOptions = {}): Promise<{ data: T; meta?: ApiMeta }> {
    const { body, headers, ...rest } = options;
    const isFormData = body instanceof FormData;

    let response: Response;
    try {
        response = await fetch(path, {
            ...rest,
            credentials: "same-origin",
            headers: {
                ...(isFormData || body === undefined ? {} : { "content-type": "application/json" }),
                ...headers,
            },
            body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
        });
    } catch {
        throw new ApiError("NETWORK_ERROR", "Không kết nối được máy chủ", 0);
    }

    if (response.status === 204) {
        return { data: undefined as T };
    }

    let payload: ApiEnvelope<T>;
    try {
        payload = (await response.json()) as ApiEnvelope<T>;
    } catch {
        throw new ApiError("BAD_RESPONSE", `Máy chủ trả về dữ liệu không hợp lệ (${response.status})`, response.status);
    }

    if (!payload.success) {
        throw new ApiError(payload.error.code, payload.error.message, response.status, payload.error.details);
    }

    return { data: payload.data, meta: payload.meta };
}

export const api = {
    get: <T>(path: string) => request<T>(path).then((result) => result.data),
    getWithMeta: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }).then((r) => r.data),
    patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }).then((r) => r.data),
    delete: <T>(path: string) => request<T>(path, { method: "DELETE" }).then((r) => r.data),
};
